import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { MdClose, MdCheck } from "react-icons/md";
import { ImSpinner2 } from "react-icons/im";
import { FiGift, FiImage, FiUsers } from "react-icons/fi";
import PanelSkeleton from "../PanelSkeleton";
import mediaUrl from "../../utils/mediaUrl";
import store from "../../redux/store";
import defaultImg from "../../assets/register.webp";
import styles from "../../styles/categoriesPanel.module.css";
import offerStyles from "../../styles/offer.module.css";
import panelStyles from "../../styles/offerPanel.module.css";

const authConfig = (extra = {}) => {
  const token = store.getState().auth?.token;
  return {
    withCredentials: true,
    ...extra,
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), ...extra.headers },
  };
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api/offer`;
const MAX_IMAGE_SIZE = 5 * 1024 * 1024;

const OfferPanel = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [form, setForm]       = useState({
    active: false,
    barText: "",
    discount: 10,
    promocode: "",
    description: "",
  });
  const [savedImageUrl, setSavedImageUrl] = useState(null);
  const [imageFile, setImageFile]         = useState(null);
  const [imagePreview, setImagePreview]   = useState(null);
  const [removeImage, setRemoveImage]     = useState(false);
  const [leads, setLeads]                 = useState([]);
  const [leadsCount, setLeadsCount]       = useState(0);
  const imgRef = useRef(null);

  const fetchOffer = async () => {
    try {
      const res = await axios.get(`${API}/dashboardFetch`, authConfig());
      const { offer, leads: l, leadsCount: c } = res.data?.data ?? {};
      if (offer) {
        setForm({
          active:      offer.active,
          barText:     offer.barText ?? "",
          discount:    offer.discount ?? 10,
          promocode:   offer.promocode ?? "",
          description: offer.description ?? "",
        });
        setSavedImageUrl(offer.imageUrl);
      }
      setLeads(l ?? []);
      setLeadsCount(c ?? 0);
    } catch {
      toast.error(<span className="toast">فشل تحميل إعدادات العرض</span>);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOffer(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > MAX_IMAGE_SIZE) {
      toast.error(<span className="toast">حجم الصورة يجب أن يكون أقل من 5 ميجابايت</span>);
      return;
    }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    setRemoveImage(false);
  };

  const clearImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setRemoveImage(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const discount = parseInt(form.discount, 10);
    if (!discount || discount < 1 || discount > 90) {
      toast.error(<span className="toast">نسبة الخصم يجب أن تكون بين 1 و 90</span>);
      return;
    }
    if (!/^[A-Za-z0-9_-]+$/.test(form.promocode.trim())) {
      toast.error(<span className="toast">كود الخصم يجب أن يحتوي على حروف إنجليزية وأرقام فقط</span>);
      return;
    }

    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("active", String(form.active));
      fd.append("barText", form.barText.trim());
      fd.append("discount", String(discount));
      fd.append("promocode", form.promocode.trim());
      fd.append("description", form.description.trim());
      if (imageFile) fd.append("imageFile", imageFile);
      else if (removeImage) fd.append("removeImage", "true");

      await axios.put(API, fd, authConfig());
      toast.success(<span className="toast">تم حفظ العرض</span>);
      setImageFile(null);
      setImagePreview(null);
      setRemoveImage(false);
      fetchOffer();
    } catch {
      toast.error(<span className="toast">فشل الحفظ. حاول مرة أخرى</span>);
    } finally {
      setSaving(false);
    }
  };

  const currentImage = imagePreview || (!removeImage && savedImageUrl ? mediaUrl(savedImageUrl) : null);

  if (loading) {
    return <div className={styles.panel}><PanelSkeleton count={3} /></div>;
  }

  return (
    <div className={styles.panel}>

      <div className={styles.panelHead}>
        <span className={styles.panelTitle}>
          <FiGift className={styles.panelTitleIcon} />
          عرض الترحيب
        </span>
      </div>

      <form className={styles.form} onSubmit={handleSubmit} encType="multipart/form-data">

        {/* Active toggle */}
        <label className={panelStyles.toggle}>
          <input
            type="checkbox"
            checked={form.active}
            onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
          />
          <span className={panelStyles.toggleTrack}><span className={panelStyles.toggleThumb} /></span>
          <span className={panelStyles.toggleLabel}>
            {form.active ? "العرض مفعّل ويظهر في الصفحة الرئيسية" : "العرض متوقف"}
          </span>
        </label>

        <div className={styles.formRow}>
          <label className={styles.formLabel}>نص الشريط *</label>
          <input
            className={styles.formInput}
            placeholder="مثال: انضم لعائلة فذكر واحصل على خصم على أول طلبية"
            maxLength={200}
            value={form.barText}
            onChange={set("barText")}
            required
          />
        </div>

        <div className={panelStyles.grid}>
          <div className={styles.formRow}>
            <label className={styles.formLabel}>نسبة الخصم % *</label>
            <input
              type="number"
              min={1}
              max={90}
              className={styles.formInput}
              value={form.discount}
              onChange={set("discount")}
              required
            />
          </div>

          <div className={styles.formRow}>
            <label className={styles.formLabel}>كود الخصم *</label>
            <input
              className={`${styles.formInput} ${panelStyles.codeInput}`}
              placeholder="WELCOME10"
              maxLength={20}
              value={form.promocode}
              onChange={(e) => setForm((f) => ({ ...f, promocode: e.target.value.toUpperCase() }))}
              required
            />
          </div>
        </div>

        <div className={styles.formRow}>
          <label className={styles.formLabel}>الوصف في النافذة (اختياري)</label>
          <input
            className={styles.formInput}
            placeholder="لوحات كانفاس إسلامية تُنير القلوب وتُزيّن البيوت"
            maxLength={300}
            value={form.description}
            onChange={set("description")}
          />
        </div>

        <div className={styles.formRow}>
          <label className={styles.formLabel}>صورة النافذة (اختياري — تُستخدم صورة افتراضية إن لم تُحدد)</label>
          <div className={panelStyles.imageRow}>
            <div className={styles.imgPreviewWrap} style={{ width: "10rem", height: "6rem" }}>
              <img src={currentImage || defaultImg} alt="" className={styles.imgPreview} />
              {currentImage && (
                <button type="button" className={styles.imgRemoveBtn} onClick={clearImage} title="استخدام الصورة الافتراضية">
                  <MdClose />
                </button>
              )}
            </div>
            <button type="button" className={styles.imgPickBtn} onClick={() => imgRef.current?.click()}>
              <FiImage className={styles.imgPickIcon} />
              {currentImage ? "تغيير الصورة" : "اختيار صورة"}
            </button>
          </div>
          <input type="file" accept="image/*" ref={imgRef} onChange={handleImageChange} style={{ display: "none" }} />
        </div>

        {/* Live preview of the bar */}
        <div className={styles.formRow}>
          <label className={styles.formLabel}>معاينة الشريط</label>
          <div className={`${offerStyles.bar} ${panelStyles.preview}`}>
            <FiGift className={offerStyles.barIcon} />
            <span className={offerStyles.barText}>{form.barText || "نص الشريط"}</span>
            <span className={offerStyles.barBadge}>خصم {form.discount || 0}%</span>
          </div>
        </div>

        <div className={styles.formActions}>
          <button type="submit" className={styles.saveBtn} disabled={saving}>
            {saving ? <ImSpinner2 className="smallspinner" /> : <><MdCheck /> حفظ</>}
          </button>
        </div>
      </form>

      <p className={styles.hint}>
        يظهر الشريط فوق القائمة في الصفحة الرئيسية للزوار غير المسجلين. عند الضغط عليه تفتح نافذة لإدخال البريد والهاتف،
        ثم يظهر لهم كود الخصم. يتم إنشاء الكود تلقائياً في أكواد الخصم بنفس النسبة.
      </p>

      {/* Leads */}
      <div className={styles.panelHead} style={{ marginTop: "1.5rem" }}>
        <span className={styles.panelTitle}>
          <FiUsers className={styles.panelTitleIcon} />
          المشتركون في العرض ({leadsCount})
        </span>
      </div>

      {leads.length === 0 ? (
        <p className="warn">لا يوجد مشتركون بعد.</p>
      ) : (
        <div className={panelStyles.tableWrap}>
          <table className={panelStyles.table}>
            <thead>
              <tr>
                <th>البريد الإلكتروني</th>
                <th>الهاتف</th>
                <th>يوافق على العروض</th>
                <th>التاريخ</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((lead) => (
                <tr key={lead.id}>
                  <td className={panelStyles.ltr}>{lead.email}</td>
                  <td className={panelStyles.ltr}>{lead.phone}</td>
                  <td>{lead.consent ? "نعم" : "لا"}</td>
                  <td>{new Date(lead.createdAt).toLocaleDateString("ar-EG")}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {leadsCount > leads.length && (
            <p className={styles.hint}>يتم عرض آخر {leads.length} مشترك.</p>
          )}
        </div>
      )}
    </div>
  );
};

export default OfferPanel;
